import React, { useState, useEffect } from 'react';
import { Drawer, List, Button, Space, Typography, Tag, Spin, Empty, Modal, message, Popconfirm } from 'antd';
import { PlayCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { sourceStoriesApi } from '../services/api';
import { SourcePanel } from '../types';

const { Text } = Typography;

interface VideoHistoryDrawerProps {
  visible: boolean;
  panel: SourcePanel;
  storyId: string;
  sceneId: string;
  onClose: () => void;
  onVideoApplied?: (updatedPanel: SourcePanel) => void;
}

interface VideoGeneration {
  id: string;
  status: string;
  videoUrl?: string;
  videoDuration?: number;
  createdAt: string;
  createdBy: string;
  errorMessage?: string;
  metadata?: {
    shotMode?: string;
    reasoning?: string;
    multiShotConfig?: any;
    singleShotConfig?: any;
  };
}

const VideoHistoryDrawer: React.FC<VideoHistoryDrawerProps> = ({
  visible,
  panel,
  storyId,
  sceneId,
  onClose,
  onVideoApplied,
}) => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<VideoGeneration[]>([]);
  const [previewVideo, setPreviewVideo] = useState<string | null>(null);
  const [applyingId, setApplyingId] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      loadHistory();
    }
  }, [visible, panel.id]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const response = await sourceStoriesApi.getVideoHistory(panel.id);
      const generations = response.data || [];
      // Sort by createdAt desc (newest first)
      generations.sort((a: VideoGeneration, b: VideoGeneration) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setHistory(generations);
    } catch (error: any) {
      message.error('Failed to load video history: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleApplyVideo = async (generationId: string) => {
    setApplyingId(generationId);
    try {
      const response = await sourceStoriesApi.applyVideoFromHistory(storyId, sceneId, panel.id, generationId);
      const updatedPanel = response.data;
      message.success('Video applied successfully!');
      onVideoApplied?.(updatedPanel);
      onClose();
    } catch (error: any) {
      message.error('Failed to apply video: ' + (error.message || 'Unknown error'));
    } finally {
      setApplyingId(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'FAILED':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'PROCESSING':
        return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
      case 'PENDING':
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      default:
        return null;
    }
  };

  const getStatusTag = (status: string) => {
    const colors: Record<string, string> = {
      COMPLETED: 'success',
      FAILED: 'error',
      PROCESSING: 'processing',
      PENDING: 'warning',
    };
    return <Tag color={colors[status] || 'default'}>{status}</Tag>;
  };

  const isCurrentVideo = (gen: VideoGeneration) => {
    return gen.videoUrl === panel.videoUrl && gen.status === 'COMPLETED';
  };

  return (
    <>
      <Drawer
        title={`📋 Lịch sử Video - Panel #${panel.sceneOrderIndex || '?'}`}
        placement="right"
        width={600}
        onClose={onClose}
        open={visible}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        ) : history.length === 0 ? (
          <Empty description="Chưa có lịch sử video" />
        ) : (
          <List
            dataSource={history}
            renderItem={(gen, index) => {
              const isCurrent = isCurrentVideo(gen);
              const metadata = gen.metadata || {};
              const shotMode = metadata.shotMode || 'unknown';
              const shotCount = metadata.multiShotConfig?.shots?.length;

              return (
                <List.Item
                  key={gen.id}
                  style={{
                    padding: '16px',
                    marginBottom: '12px',
                    border: isCurrent ? '2px solid #52c41a' : '1px solid #e8e8e8',
                    borderRadius: '8px',
                    background: isCurrent ? '#f6ffed' : '#fff',
                  }}
                >
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space>
                      {getStatusIcon(gen.status)}
                      <Text strong>
                        #{history.length - index} {isCurrent && '(Đang dùng)'}
                      </Text>
                      {getStatusTag(gen.status)}
                    </Space>

                    <Space wrap>
                      <Text type="secondary">
                        {shotMode === 'single' && 'Single-shot'}
                        {shotMode === 'multi' && `Multi-shot (${shotCount || '?'} shots)`}
                      </Text>
                      {gen.videoDuration && (
                        <>
                          <Text type="secondary">|</Text>
                          <Text type="secondary">{gen.videoDuration}s</Text>
                        </>
                      )}
                      <Text type="secondary">|</Text>
                      <Text type="secondary">
                        {new Date(gen.createdAt).toLocaleString('vi-VN')}
                      </Text>
                      <Text type="secondary">by {gen.createdBy}</Text>
                    </Space>

                    {metadata.reasoning && (
                      <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                        {metadata.reasoning}
                      </Text>
                    )}

                    {gen.status === 'FAILED' && gen.errorMessage && (
                      <Text type="danger" style={{ fontSize: 12 }}>
                        Error: {gen.errorMessage}
                      </Text>
                    )}

                    {gen.status === 'COMPLETED' && gen.videoUrl && (
                      <Space>
                        <Button
                          size="small"
                          icon={<PlayCircleOutlined />}
                          onClick={() => setPreviewVideo(gen.videoUrl!)}
                        >
                          Xem
                        </Button>
                        {!isCurrent && (
                          <Popconfirm
                            title="Chọn lại video này?"
                            description="Video hiện tại sẽ được thay thế bằng video này."
                            onConfirm={() => handleApplyVideo(gen.id)}
                            okText="Chọn lại"
                            cancelText="Hủy"
                          >
                            <Button
                              type="primary"
                              size="small"
                              loading={applyingId === gen.id}
                            >
                              ✅ Chọn lại
                            </Button>
                          </Popconfirm>
                        )}
                      </Space>
                    )}
                  </Space>
                </List.Item>
              );
            }}
          />
        )}
      </Drawer>

      <Modal
        open={!!previewVideo}
        onCancel={() => setPreviewVideo(null)}
        footer={null}
        width={400}
        centered
        destroyOnClose
        bodyStyle={{ padding: 0, background: '#000', borderRadius: '8px', overflow: 'hidden' }}
      >
        {previewVideo && (
          <div style={{ width: '100%', aspectRatio: '9/16', background: '#000' }}>
            <video 
              src={previewVideo} 
              controls 
              autoPlay 
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default VideoHistoryDrawer;
